import { useState, useEffect, useRef, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Copy, Download, Trash2, X, ArrowRight, ChevronRight, Layers, Search, Loader2 } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import { dialog } from '../../store/dialogStore';
import useToastStore from '../../store/toastStore';
import { formatBRL, sumCurrency } from '../../utils/currency';

function downloadCsv(filename, rows) {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(escape).join(',')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function MovePopup({ selectedIds, currentBoard, groups, onDone, onClose, onMoveStart, onMoveSuccess, onMoveError }) {
  const fetchAllBoards = useBoardStore((s) => s.fetchAllBoards);
  const moveSelectedCards = useBoardStore((s) => s.moveSelectedCards);
  const [step, setStep] = useState('root'); // root | group | quadro | board-groups
  const [allBoards, setAllBoards] = useState([]);
  const [boardGroups, setBoardGroups] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [boardSearch, setBoardSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const ref = useRef(null);
  const suppressOutsideRef = useRef(false);

  useEffect(() => {
    const handler = (e) => {
      if (suppressOutsideRef.current) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const runMove = async (groupId, isCrossBoard) => {
    setLoading(true);
    onMoveStart?.();
    try {
      const { originalGroups } = await moveSelectedCards(selectedIds, groupId, isCrossBoard);
      onMoveSuccess?.({ targetGroupId: groupId, isCrossBoard, originalGroups });
      onDone();
    } catch (err) {
      onMoveError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const confirmAndMove = async ({ groupId, isCrossBoard, groupName, boardName }) => {
    const n = selectedIds.size;
    const itensLabel = `${n} ${n === 1 ? 'item' : 'itens'}`;
    // Suprime o auto-close do popup (clique fora) enquanto o diálogo está aberto.
    suppressOutsideRef.current = true;
    let ok = false;
    try {
      ok = await dialog.confirm(
        'Mover itens',
        `Você deseja mover os ${itensLabel} selecionados para o grupo "${groupName}" do quadro "${boardName}"?`,
        { confirmLabel: 'Confirmar', cancelLabel: 'Cancelar' }
      );
    } finally {
      suppressOutsideRef.current = false;
    }
    if (!ok) return; // cancelar mantém o MovePopup aberto
    runMove(groupId, isCrossBoard);
  };

  const handleToGroup = (groupId) => {
    const g = currentGroups.find((x) => x.id === groupId);
    confirmAndMove({
      groupId,
      isCrossBoard: false,
      groupName: g?.name || '',
      boardName: currentBoard?.name || '',
    });
  };

  const handleLoadBoards = async () => {
    setStep('quadro');
    setLoading(true);
    try {
      const data = await fetchAllBoards();
      setAllBoards(data.filter((b) => b.id !== currentBoard?.id));
    } finally { setLoading(false); }
  };

  const handleSelectBoard = async (board) => {
    setSelectedBoard(board);
    setGroupSearch('');
    setStep('board-groups');
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/boards/${board.id}/groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const json = await res.json();
      setBoardGroups(json.data || []);
    } finally { setLoading(false); }
  };

  const handleToBoardGroup = (groupId) => {
    const g = boardGroups.find((x) => x.id === groupId);
    confirmAndMove({
      groupId,
      isCrossBoard: true,
      groupName: g?.name || '',
      boardName: selectedBoard?.name || '',
    });
  };

  const currentGroups = groups.filter((g) => !g.is_divider);

  return (
    <div
      ref={ref}
      className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#1e2235] border border-border rounded-xl shadow-2xl w-[307px] overflow-hidden z-50"
    >
      {step === 'root' && (
        <>
          <div className="px-4 py-2.5 text-[13px] font-semibold text-text-secondary uppercase tracking-wide border-b border-border/50">
            Mover {selectedIds.size} {selectedIds.size === 1 ? 'item' : 'itens'}
          </div>
          <button
            onClick={() => { setStep('group'); setGroupSearch(''); }}
            className="w-full flex items-center justify-between px-5 py-3.5 text-[15px] text-text-primary hover:bg-white/5 transition-colors"
          >
            <span className="flex items-center gap-2.5"><Layers className="w-5 h-5 text-text-secondary" /> Para grupo</span>
            <ChevronRight className="w-5 h-5 text-text-secondary" />
          </button>
          <button
            onClick={handleLoadBoards}
            className="w-full flex items-center justify-between px-5 py-3.5 text-[15px] text-text-primary hover:bg-white/5 transition-colors"
          >
            <span className="flex items-center gap-2.5"><ArrowRight className="w-5 h-5 text-text-secondary" /> Para quadro</span>
            <ChevronRight className="w-5 h-5 text-text-secondary" />
          </button>
        </>
      )}

      {step === 'group' && (
        <>
          <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border/50">
            <button onClick={() => { setStep('root'); setGroupSearch(''); }} className="text-text-secondary hover:text-text-primary">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wide">Escolher grupo</span>
          </div>
          <div className="px-4 py-2.5 border-b border-border/50">
            <div className="flex items-center gap-2.5 bg-main border border-border rounded-lg px-3.5 py-2">
              <Search className="w-4 h-4 text-text-secondary shrink-0" />
              <input
                autoFocus
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                placeholder="Pesquisar grupo"
                className="flex-1 bg-transparent outline-none text-[15px] text-text-primary placeholder-text-secondary/50"
              />
            </div>
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            {currentGroups.filter((g) => g.name.toLowerCase().includes(groupSearch.toLowerCase())).map((g) => (
              <button
                key={g.id}
                onClick={() => handleToGroup(g.id)}
                disabled={loading}
                className="w-full flex items-center gap-3.5 px-5 py-3 text-[15px] text-text-primary hover:bg-white/5 transition-colors"
              >
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                {g.name}
              </button>
            ))}
          </div>
        </>
      )}

      {step === 'quadro' && (
        <>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <span className="text-[15px] font-semibold text-text-primary">Escolher quadro</span>
            <button onClick={() => setStep('root')} className="text-sm text-text-secondary hover:text-text-primary transition-colors">Voltar</button>
          </div>
          <div className="px-4 py-2.5 border-b border-border/50">
            <div className="flex items-center gap-2.5 bg-main border border-border rounded-lg px-3.5 py-2">
              <Search className="w-4 h-4 text-text-secondary shrink-0" />
              <input
                autoFocus
                value={boardSearch}
                onChange={(e) => setBoardSearch(e.target.value)}
                placeholder="Pesquisar quadro"
                className="flex-1 bg-transparent outline-none text-[15px] text-text-primary placeholder-text-secondary/50"
              />
            </div>
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            {loading ? (
              <div className="py-5 text-center text-sm text-text-secondary">Carregando...</div>
            ) : allBoards.filter((b) => b.name.toLowerCase().includes(boardSearch.toLowerCase())).map((b) => (
              <button
                key={b.id}
                onClick={() => handleSelectBoard(b)}
                className="w-full flex items-center justify-between gap-3.5 px-5 py-3 text-[15px] text-text-primary hover:bg-white/5 transition-colors"
              >
                <span className="truncate">{b.name}</span>
                <ChevronRight className="w-4 h-4 text-text-secondary shrink-0" />
              </button>
            ))}
          </div>
        </>
      )}

      {step === 'board-groups' && (
        <>
          <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border/50">
            <button onClick={() => setStep('quadro')} className="text-text-secondary hover:text-text-primary">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wide truncate">{selectedBoard?.name}</span>
          </div>
          <div className="px-4 py-2.5 border-b border-border/50">
            <div className="flex items-center gap-2.5 bg-main border border-border rounded-lg px-3.5 py-2">
              <Search className="w-4 h-4 text-text-secondary shrink-0" />
              <input
                autoFocus
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                placeholder="Pesquisar grupo"
                className="flex-1 bg-transparent outline-none text-[15px] text-text-primary placeholder-text-secondary/50"
              />
            </div>
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            {loading ? (
              <div className="py-5 text-center text-sm text-text-secondary">Carregando...</div>
            ) : boardGroups.filter((g) => !g.is_divider && g.name.toLowerCase().includes(groupSearch.toLowerCase())).map((g) => (
              <button
                key={g.id}
                onClick={() => handleToBoardGroup(g.id)}
                disabled={loading}
                className="w-full flex items-center gap-3.5 px-5 py-3 text-[15px] text-text-primary hover:bg-white/5 transition-colors"
              >
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                {g.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Item do resumo da seleção: nome da coluna pequeno em cima, valor embaixo.
// O nome trunca (max-w); o valor nunca, pra não exibir um total cortado.
function SummaryMetric({ label, value }) {
  return (
    <div className="flex flex-col shrink-0 leading-tight">
      <span className="max-w-[7rem] truncate text-[11px] text-text-secondary">{label}</span>
      <span className="text-sm leading-tight font-semibold text-text-primary tabular-nums whitespace-nowrap">{value}</span>
    </div>
  );
}

export default function SelectionBar({ selectedIds, onClear, hiddenColumns }) {
  const { currentBoard, cards, columns, cardValues, statusOptions, users, groups } = useBoardStore(
    useShallow((s) => ({
      currentBoard: s.currentBoard,
      cards: s.cards,
      columns: s.columns,
      cardValues: s.cardValues,
      statusOptions: s.statusOptions,
      users: s.users,
      groups: s.groups,
    }))
  );
  const duplicateCard = useBoardStore((s) => s.duplicateCard);
  const bulkArchiveCards = useBoardStore((s) => s.bulkArchiveCards);
  const bulkUnarchiveCards = useBoardStore((s) => s.bulkUnarchiveCards);
  const moveSelectedCards = useBoardStore((s) => s.moveSelectedCards);
  const showToast = useToastStore((s) => s.show);

  const handleMoveSuccess = ({ isCrossBoard, originalGroups }) => {
    const total = originalGroups.size;
    if (total === 0) return;

    // Agrupa por grupo original para reverter em batches (1 request por grupo origem)
    const byOrigin = new Map();
    for (const [cardId, originGroupId] of originalGroups.entries()) {
      if (!byOrigin.has(originGroupId)) byOrigin.set(originGroupId, []);
      byOrigin.get(originGroupId).push(cardId);
    }

    showToast({
      message: `${total} ${total === 1 ? 'item movido' : 'itens movidos'}`,
      onUndo: async () => {
        // Reverte chamando moveSelectedCards uma vez por grupo origem.
        // Cross-board reverso (cards saíram do board) também é tratado: o
        // backend bulkMove faz remap de card_values entre boards.
        for (const [originGroupId, ids] of byOrigin.entries()) {
          try {
            await moveSelectedCards(new Set(ids), originGroupId, isCrossBoard);
          } catch {
            /* erro de undo é silencioso — usuário pode recarregar */
          }
        }
      },
    });
  };

  const handleMoveError = () => {
    showToast({
      message: 'Falha ao mover. Tente novamente.',
      onUndo: null,
      duration: 4000,
    });
  };

  const [moveOpen, setMoveOpen] = useState(false);
  const [dupBusy, setDupBusy] = useState({ active: false, done: 0, total: 0 });
  const [moveBusy, setMoveBusy] = useState(false);
  const count = selectedIds.size;

  // Itens selecionados e total de cada coluna de moeda, somado só desses itens
  // (valor negativo subtrai). Coluna oculta no toolbar ("Ocultar") fica fora do
  // resumo, senão ele vazaria o total do que o usuário escondeu. Ficam antes do
  // early return por causa da regra dos hooks.
  const selectedCards = useMemo(
    () => (selectedIds.size === 0 ? [] : cards.filter((c) => selectedIds.has(c.id))),
    [cards, selectedIds]
  );
  const currencyTotals = useMemo(
    () => columns
      .filter((c) => c.type === 'currency' && !hiddenColumns?.has(c.id))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((col) => {
        // Mesma regra do rodapé do grupo: só número finito entra na conta e, sem nenhum
        // valor na seleção, "—" em vez de "R$ 0,00". As casas vão cruas de config.decimals:
        // sumCurrency e formatBRL normalizam (fora de 0..6 vale 2).
        const amounts = [];
        for (const card of selectedCards) {
          const v = cardValues[`${card.id}:${col.id}`];
          if (typeof v === 'number' && Number.isFinite(v)) amounts.push(v);
        }
        const decimals = col.config?.decimals;
        return {
          id: col.id,
          name: col.name,
          formatted: amounts.length ? formatBRL(sumCurrency(amounts, decimals), decimals) : '—',
        };
      }),
    [columns, hiddenColumns, selectedCards, cardValues]
  );

  if (count === 0 && !dupBusy.active && !moveBusy) return null;

  const summaryTitle = [`Itens: ${count}`, ...currencyTotals.map((t) => `${t.name}: ${t.formatted}`)].join(' · ');

  // M2b, item 4: duplicar cartão é 1 requisição ao servidor por cartão
  // (endpoint dedicado — nasce com título, grupo e valores já prontos, sem a
  // sequência criar+upsert-por-coluna de antes, que gerava um evento
  // sintético de mudança de valor por coluna preenchida). Item 5: um
  // X-Batch-Id só para o gesto inteiro — N cartões duplicados de uma vez
  // aparecem como um lote no histórico, não N ações avulsas.
  const handleDuplicate = async () => {
    setDupBusy({ active: true, done: 0, total: selectedCards.length });
    const batchId = crypto.randomUUID();
    try {
      for (let i = 0; i < selectedCards.length; i++) {
        await duplicateCard(selectedCards[i].id, { batchId });
        setDupBusy({ active: true, done: i + 1, total: selectedCards.length });
      }
      onClear();
    } finally {
      setDupBusy({ active: false, done: 0, total: 0 });
    }
  };

  const handleExport = () => {
    const sortedCols = [...columns].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const header = ['Elemento', ...sortedCols.map((c) => c.name)];
    const renderValue = (col, v) => {
      if (v == null) return '';
      if (col.type === 'person') { const u = users.find((x) => x.id === v); return u?.name || ''; }
      if (col.type === 'status') {
        const opts = statusOptions[col.id] || [];
        // M3c: id primeiro (formato canônico {id,label}); fallback pro label
        // cru quando o valor é string legada ou {label} sem id correspondente.
        const rawLabel = v && typeof v === 'object' ? (v.label ?? null) : (typeof v === 'string' ? v : null);
        const match = (v?.id != null && opts.find((o) => o.id === v.id))
          || (rawLabel != null && opts.find((o) => o.label === rawLabel));
        return match?.label || rawLabel || '';
      }
      if (col.type === 'link') return typeof v === 'string' ? v : v?.url || '';
      if (col.type === 'checkbox') return v ? 'sim' : 'não';
      if (col.type === 'currency' && typeof v === 'number') {
        // Texto como a tela mostra ("R$ 1.234,56", casas da coluna): planilha com locale
        // pt-BR lê como moeda e soma; "1234.56" entraria como texto. A vírgula decimal
        // sai entre aspas pelo escape do downloadCsv.
        return formatBRL(v, col.config?.decimals);
      }
      return typeof v === 'object' ? JSON.stringify(v) : String(v);
    };
    const rows = [header];
    for (const card of selectedCards) {
      rows.push([card.title, ...sortedCols.map((col) => renderValue(col, cardValues[`${card.id}:${col.id}`]))]);
    }
    downloadCsv(`${currentBoard?.name?.replace(/[^\w\- ]+/g, '_') || 'quadro'}.csv`, rows);
  };

  const handleArchive = async () => {
    if (!(await dialog.confirm('Mover para a lixeira', `Mover ${count} ${count === 1 ? 'item' : 'itens'} para a lixeira? Você pode restaurar depois.`, { confirmLabel: 'Mover', danger: true }))) return;
    const archivedIds = [...selectedIds];
    onClear();
    bulkArchiveCards(archivedIds).catch(() => {});
    showToast({
      message: `${archivedIds.length} ${archivedIds.length === 1 ? 'elemento excluído' : 'elementos excluídos'} com sucesso`,
      onUndo: async () => {
        try { await bulkUnarchiveCards(archivedIds); } catch {}
      },
    });
  };

  return (
    <>
      {dupBusy.active && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center cursor-wait"
          onPointerDown={(e) => e.preventDefault()}
          onKeyDown={(e) => e.preventDefault()}
        >
          <div className="bg-card border border-border rounded-xl px-6 py-5 shadow-2xl flex items-center gap-4 min-w-[280px]">
            <Loader2 className="w-6 h-6 animate-spin text-accent shrink-0" />
            <div className="flex-1">
              <div className="text-sm text-text-primary font-medium">
                Duplicando {dupBusy.total} {dupBusy.total === 1 ? 'item' : 'itens'}...
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                {dupBusy.done} de {dupBusy.total} concluídos
              </div>
              <div className="h-1 bg-white/10 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${(dupBusy.done / dupBusy.total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 md:w-max md:max-w-[calc(100vw-2rem)]">
      {moveOpen && (
        <MovePopup
          selectedIds={selectedIds}
          currentBoard={currentBoard}
          groups={groups}
          onDone={() => { setMoveOpen(false); setMoveBusy(false); onClear(); }}
          onClose={() => { if (!moveBusy) setMoveOpen(false); }}
          onMoveStart={() => setMoveBusy(true)}
          onMoveSuccess={handleMoveSuccess}
          onMoveError={(err) => { setMoveBusy(false); handleMoveError(err); }}
        />
      )}
      <div className={`bg-card border border-border rounded-xl shadow-2xl flex items-center overflow-hidden ${moveBusy ? 'pointer-events-none opacity-80' : ''}`}>
        <div className="flex items-center gap-2 px-4 py-3 bg-accent text-white md:shrink-0">
          <span className="w-7 h-7 rounded-full bg-white text-accent flex items-center justify-center text-sm font-bold">{count}</span>
          <span className="text-sm font-medium">{count === 1 ? 'item selecionado' : 'itens selecionados'}</span>
        </div>
        <div className="flex items-center md:min-w-0">
          <button
            onClick={handleDuplicate}
            disabled={dupBusy.active || moveBusy}
            className="flex flex-col items-center gap-1 px-4 py-3 text-xs text-text-primary hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Copy className="w-4 h-4" />
            Duplicar
          </button>
          <button onClick={handleExport} disabled={moveBusy} className="flex flex-col items-center gap-1 px-4 py-3 text-xs text-text-primary hover:bg-white/5 transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" />
            Exportar
          </button>
          <button
            onClick={() => setMoveOpen((v) => !v)}
            disabled={moveBusy}
            className={`flex flex-col items-center gap-1 px-4 py-3 text-xs transition-colors disabled:cursor-not-allowed ${moveOpen ? 'text-accent bg-accent/10' : 'text-text-primary hover:bg-white/5'}`}
          >
            {moveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Mover
          </button>
          <button onClick={handleArchive} disabled={moveBusy} className="flex flex-col items-center gap-1 px-4 py-3 text-xs text-text-primary hover:bg-white/5 transition-colors disabled:opacity-50">
            <Trash2 className="w-4 h-4" />
            Lixeira
          </button>
          {/* Resumo da seleção, estilo barra de status do Excel. Só de md pra cima:
              abaixo disso as próprias ações já ocupam a tela. flex-wrap + h-8 +
              overflow-hidden: o item que não couber cai numa 2ª linha escondida,
              em vez de aparecer cortado pela metade. */}
          <div className="hidden md:block w-px h-10 bg-border mx-1 shrink-0" />
          <div
            role="group"
            aria-label="Resumo da seleção"
            title={summaryTitle}
            className="hidden md:flex flex-wrap content-start items-center gap-5 h-8 px-3 min-w-0 max-w-[42rem] overflow-hidden"
          >
            <SummaryMetric label={count === 1 ? 'Item' : 'Itens'} value={count} />
            {currencyTotals.map((t) => (
              <SummaryMetric key={t.id} label={t.name} value={t.formatted} />
            ))}
          </div>
          <div className="w-px h-10 bg-border mx-1 md:shrink-0" />
          <button onClick={onClear} disabled={moveBusy} className="p-3 text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors disabled:opacity-50" title="Limpar seleção">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
